import { z } from "zod";
import { id, isoDate, moneyMinor, nullableId, optionalText } from "../schemas/common";
import { MAX_TAX_RATE_BP } from "../tax";
import { COST_TYPES, defaultPurpose, EXPENSE_PURPOSES } from "./expenses";
import { FUEL_UNITS, isFillUpType, unitsFor } from "./fill-ups";

const nullableInt = (schema: z.ZodInt) => schema.nullish().transform((v) => v ?? null);

/**
 * An expense as entered (reference.md › The module's API). The amount is on the garage's price
 * basis; without a rate the garage's on the date applies, and without a purpose the cost type's.
 * Only the cost type and the amount are needed besides the date: payee, vehicle and the rest are
 * optional, so a quick fuel entry stays quick.
 */
export const expenseInput = z
  .object({
    vehicleId: nullableId,
    spentOn: isoDate,
    costType: z.enum(COST_TYPES),
    purpose: z
      .enum(EXPENSE_PURPOSES)
      .nullish()
      .transform((v) => v ?? null),
    amountMinor: moneyMinor.refine((v) => v > 0, { error: "validation.positive" }),
    taxRateBp: nullableInt(z.int({ error: "validation.number" }).min(0).max(MAX_TAX_RATE_BP)),
    supplierId: nullableId,
    payeeName: optionalText,
    workOrderId: nullableId,
    mileage: nullableInt(
      z
        .int({ error: "validation.number" })
        .nonnegative({ error: "validation.nonNegative" })
        .max(10_000_000, { error: "validation.tooLarge" }),
    ),
    fuelQty: nullableInt(
      z
        .int({ error: "validation.number" })
        .positive({ error: "validation.positive" })
        .max(1_000_000_000, { error: "validation.tooLarge" }),
    ),
    fuelUnit: z
      .enum(FUEL_UNITS)
      .nullish()
      .transform((v) => v ?? null),
    invoiceNumber: optionalText,
    comment: optionalText,
  })
  .superRefine((e, ctx) => {
    if (e.mileage !== null && e.vehicleId === null) {
      ctx.addIssue({
        code: "custom",
        path: ["mileage"],
        message: "validation.mileageNeedsVehicle",
      });
    }
    if ((e.fuelQty === null) !== (e.fuelUnit === null)) {
      const path = e.fuelQty === null ? "fuelQty" : "fuelUnit";
      ctx.addIssue({ code: "custom", path: [path], message: "validation.required" });
    }
    if (e.fuelUnit !== null) {
      const fits = isFillUpType(e.costType) && unitsFor(e.costType).includes(e.fuelUnit);
      if (!fits)
        ctx.addIssue({ code: "custom", path: ["fuelUnit"], message: "validation.fuelUnit" });
    }
  })
  .transform((e) => ({ ...e, purpose: e.purpose ?? defaultPurpose(e.costType) }));

export type ExpenseInput = z.input<typeof expenseInput>;
export type ExpenseData = z.output<typeof expenseInput>;

export const expenseUpdateInput = z.object({ id, data: expenseInput });
